import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { setAccessToken, setPrimaryColor, applyPrimaryColor } from "@/lib/auth";
import { APP_NAME } from "@/lib/brand";
import { registerSchema, type RegisterFormData } from "@/validators/auth";
import type { AuthTokens } from "@/types/auth";

const REGISTRATION_OPEN = import.meta.env["VITE_REGISTRATION_OPEN"] !== "false";

export function RegisterPage() {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  if (!REGISTRATION_OPEN) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-4 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {APP_NAME}
          </h1>
          <p className="text-sm text-muted-foreground">
            O cadastro de novas transportadoras está temporariamente
            indisponível.
          </p>
          <Link
            to="/login"
            className="inline-block text-sm font-medium text-foreground underline-offset-4 hover:underline"
          >
            Ir para o login
          </Link>
        </div>
      </div>
    );
  }

  async function onSubmit(data: RegisterFormData) {
    setApiError(null);
    try {
      const res = await api.post<AuthTokens>("/auth/register", data);
      setAccessToken(res.data.accessToken);
      setPrimaryColor(res.data.primaryColor);
      applyPrimaryColor(res.data.primaryColor);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (isAxiosError(err)) {
        const msg = err.response?.data?.error?.message as string | undefined;
        setApiError(msg ?? "Erro ao criar conta. Tente novamente.");
      } else {
        setApiError("Erro inesperado. Tente novamente.");
      }
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {APP_NAME}
          </h1>
          <p className="text-sm text-muted-foreground">
            Cadastre sua transportadora
          </p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="space-y-4"
        >
          <fieldset className="space-y-4">
            <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Dados da Transportadora
            </legend>

            <div className="space-y-1.5">
              <Label htmlFor="tenantName">Nome da Transportadora</Label>
              <Input
                id="tenantName"
                placeholder="Transportadora ABC"
                aria-invalid={!!errors.tenantName}
                {...register("tenantName")}
              />
              {errors.tenantName && (
                <p className="text-xs text-destructive">
                  {errors.tenantName.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cnpj">CNPJ</Label>
              <Input
                id="cnpj"
                placeholder="14 dígitos sem pontuação"
                maxLength={14}
                aria-invalid={!!errors.cnpj}
                {...register("cnpj")}
              />
              {errors.cnpj && (
                <p className="text-xs text-destructive">
                  {errors.cnpj.message}
                </p>
              )}
            </div>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Seu Acesso
            </legend>

            <div className="space-y-1.5">
              <Label htmlFor="name">Seu Nome</Label>
              <Input
                id="name"
                placeholder="João Silva"
                aria-invalid={!!errors.name}
                {...register("name")}
              />
              {errors.name && (
                <p className="text-xs text-destructive">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="seu@email.com"
                aria-invalid={!!errors.email}
                {...register("email")}
              />
              {errors.email && (
                <p className="text-xs text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                placeholder="Mínimo 8 caracteres"
                aria-invalid={!!errors.password}
                {...register("password")}
              />
              {errors.password && (
                <p className="text-xs text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>
          </fieldset>

          {apiError && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {apiError}
            </p>
          )}

          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Criando conta…" : "Criar conta"}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Já tem conta?{" "}
          <Link
            to="/login"
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
}
